import { useCallback, useEffect, useState } from "react";
import { fetchFastApi } from "../api/fastapi";
import { toast } from "sonner";
import { usePresignedUpload } from "./usePresignedUpload";
import {
  PersistedWorkStepDocumentInfoSchema,
  WorkStepDocumentInfo,
} from "../models/audit/collabWorkStepSession";

/**
 * The documents attached to a persisted work step (its `work_step_document` rows),
 * plus attach and remove operations — the work-step twin of `useControlDocuments`.
 * Uploads attach to the work step as supplementary evidence, and removals delete
 * the junction (the backend reports whether the underlying document was detached
 * or deleted outright).
 *
 * Returns the same shape as `useCollabWorkStepSessionDocuments` so the collab page
 * can drive its Context Documents panel from either source.
 */
export const useWorkStepDocuments = (workStepId: string | null) => {
  const [documents, setDocuments] = useState<WorkStepDocumentInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [mutating, setMutating] = useState(false);
  const { uploadFiles } = usePresignedUpload();

  const fetchDocuments = useCallback(async () => {
    if (!workStepId) {
      setDocuments([]);
      return;
    }
    setLoading(true);
    try {
      const response = await fetchFastApi({
        path: `/work-steps/work-step/${workStepId}/documents`,
        method: "GET",
      });
      setDocuments(PersistedWorkStepDocumentInfoSchema.array().parse(response));
    } catch (error) {
      console.error("Failed to fetch work step documents:", error);
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [workStepId]);

  useEffect(() => {
    void fetchDocuments();
  }, [fetchDocuments]);

  const attachDocuments = useCallback(
    async (files: File[]) => {
      if (!workStepId || files.length === 0) return;
      setMutating(true);
      try {
        const uploadResult = await uploadFiles(files);

        const formData = new FormData();
        if (uploadResult.usedPresigned) {
          formData.append("upload_ids", uploadResult.uploadIds.join(","));
        } else {
          uploadResult.files.forEach((file) => formData.append("files", file));
        }
        formData.append("doc_type", "supplementary");

        await fetchFastApi({
          path: `/work-steps/work-step/${workStepId}/documents`,
          method: "POST",
          body: formData,
        });
        // Refetch rather than optimistically append: the list is the source of truth
        // for the work_step_document_ids and parse states the rows now have.
        await fetchDocuments();
      } catch (error) {
        console.error("Failed to attach work step documents:", error);
        toast.error("Failed to upload documents");
      } finally {
        setMutating(false);
      }
    },
    [workStepId, uploadFiles, fetchDocuments],
  );

  const removeDocument = useCallback(
    async (workStepDocumentId: string) => {
      if (!workStepId) return;
      // Optimistic; refetch on failure restores the true set.
      setDocuments((prev) =>
        prev.filter((doc) => doc.workStepDocumentId !== workStepDocumentId),
      );
      try {
        await fetchFastApi({
          path: `/work-steps/work-step/${workStepId}/documents/${workStepDocumentId}`,
          method: "DELETE",
        });
      } catch (error) {
        console.error("Failed to remove work step document:", error);
        toast.error("Failed to remove document");
        await fetchDocuments();
      }
    },
    [workStepId, fetchDocuments],
  );

  return {
    documents,
    loading,
    mutating,
    refetch: fetchDocuments,
    attachDocuments,
    removeDocument,
  };
};
